/**
 * Perfil do tenant no SPA (spec 017): vocabulário, recurso e marca vêm do /me
 * — equipe E cliente do portal — e as telas não escrevem mais palavra de
 * vertical nenhuma (a guarda em apps/api/src/shared/vertical-vocabulary.test.ts
 * reprova "placa", "associado", "pooltrack"… fora dos presets do @erp/shared).
 *
 * Enquanto o /me não chega, vale o último perfil visto NESTE navegador
 * (localStorage, conveniência por pessoa) e, sem ele, o vocabulário neutro do
 * produto — nunca o de um vertical. Sem o cache, a marca piscava "FatorH ERP"
 * antes do nome do tenant em todo carregamento.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_TENANT_PROFILE,
  ResolvedTenantProfileSchema,
  capitalize,
  resolveTenantProfile,
  type Brand,
  type ResolvedTenantProfile,
  type Terms,
} from '@erp/shared';
import { profileQueryOptions } from '@/features/account/account-queries';
import { isImpersonationPage } from '@/auth/impersonation';

const CACHE_KEY = 'erp.tenantProfile';

/** Genérico + marca do produto: o que se mostra sem saber o tenant. */
export const NEUTRAL_PROFILE: ResolvedTenantProfile = resolveTenantProfile(DEFAULT_TENANT_PROFILE, {
  name: null,
  logoUrl: null,
});

function readCached(): ResolvedTenantProfile | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = ResolvedTenantProfileSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeCached(profile: ResolvedTenantProfile): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(profile));
  } catch {
    /* navegação privada/armazenamento bloqueado: só perde o atalho */
  }
}

/** Perfil resolvido do tenant ativo (FR-004). */
export function useTenantProfile(): ResolvedTenantProfile {
  const profileQ = useQuery({ ...profileQueryOptions(), staleTime: 60_000 });
  const fresh = profileQ.data?.tenantProfile;
  useEffect(() => {
    // a aba de depuração (spec 020) não mexe no que é do navegador do admin
    if (fresh && !isImpersonationPage()) writeCached(fresh);
  }, [fresh]);
  return fresh ?? readCached() ?? NEUTRAL_PROFILE;
}

/** Dicionário de termos do tenant (forma do meio da frase — use `capitalize` em rótulo). */
export function useTerms(): Terms {
  return useTenantProfile().terms;
}

export function useBrand(): Brand {
  return useTenantProfile().brand;
}

/**
 * Texto da busca do topo e da tela de busca. O identificador do recurso só
 * entra quando a busca de fato o encontra: é a placa extraída do ticket
 * (spec 012) — equipamento cadastrado não é buscado por ali.
 */
export function ticketSearchHint(profile: ResolvedTenantProfile): string {
  return profile.resource.source === 'ticket_identifier'
    ? `nome, ${profile.terms.resourceId} ou telefone`
    : 'nome ou telefone';
}

export { capitalize };
